import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Unlock, Copy, Check, AlertTriangle, Flame, Loader2, ShieldX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { publicApi as api } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { deriveSmsV3Keys, unwrapSmsV3, decryptWithKey, SMS_V3_VERSION } from "../lib/crypto";
import { OralCodeInput } from "./OralCodeInput";
import { useTranslation } from "react-i18next";

const Wrap = ({ children }) => (
  <div className="min-h-screen bg-[#080B10] flex items-start justify-center px-4 py-8">
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="w-full max-w-md" data-testid="sms-delivery-page">
      <div className="flex items-center gap-2 mb-6 justify-center">
        <ShieldCheck className="w-6 h-6 text-emerald-400" />
        <span className="font-heading font-bold text-white text-xl">SecurePass</span>
      </div>
      {children}
    </motion.div>
  </div>
);

const Notice = ({ icon: Icon, tone, text, testid }) => (
  <Wrap><div className="rounded-2xl bg-slate-900 border border-slate-800 p-8 text-center" data-testid={testid}>
    <Icon className={`w-10 h-10 mx-auto mb-3 ${tone}`} />
    <p className="text-slate-300">{text}</p></div></Wrap>
);

export default function SmsDeliveryPage() {
  const { token } = useParams();
  const { t } = useTranslation("sms");
  const [meta, setMeta] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [step, setStep] = useState("code"); // code | ready | done | locked
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [secret, setSecret] = useState(null);
  const [copied, setCopied] = useState(false);
  const kSecretRef = useRef(null);
  const revealTokenRef = useRef(null);

  useEffect(() => {
    api.get(`/s/${token}`).then(({ data }) => {
      if (data.status === "active" && data.crypto_version !== SMS_V3_VERSION) { setLoadErr(true); return; }
      setMeta(data);
      setRemaining(data.attempts_remaining ?? null);
    }).catch(() => setLoadErr(true));
  }, [token]);

  // Dérivation locale (Argon2id + HKDF) -> preuve K_verify -> le serveur libère le wrapped material.
  const unlock = async (value = code) => {
    if (value.length !== 9 || checking) return;
    setChecking(true);
    setError(null);
    try {
      const { kWrap, verifier } = await deriveSmsV3Keys(value, meta.salt, meta.argon2_params);
      const { data } = await api.post(`/s/${token}/unlock`, { verifier_client: verifier });
      kSecretRef.current = await unwrapSmsV3(kWrap, data);
      revealTokenRef.current = data.reveal_token;
      setCode("");
      setStep("ready");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 423) { setStep("locked"); return; }
      if (d && typeof d === "object") {
        setRemaining(d.attempts_remaining);
        setError(d.code === "INVALID_CODE" ? t("recipient.errInvalidCode") : t("recipient.errUnlock"));
      } else {
        setError(t("recipient.errUnlock"));
      }
      setCode("");
    } finally {
      setChecking(false);
    }
  };

  const reveal = async () => {
    setRevealing(true);
    try {
      const { data } = await api.post(`/s/${token}/reveal`, {}, { headers: { "X-Recipient-Reveal-Token": revealTokenRef.current } });
      const { password } = await decryptWithKey(kSecretRef.current, { ciphertext: data.ciphertext, iv: data.iv });
      kSecretRef.current = null;
      setSecret(password);
      setStep("done");
    } catch (e) {
      toast.error(t("recipient.errUnavailable"));
    } finally {
      setRevealing(false);
    }
  };

  const copySecret = async () => {
    const ok = await copyToClipboard(secret);
    if (!ok) { toast.error(t("recipient.errCopy")); return; }
    setCopied(true); toast.success(t("recipient.copied")); setTimeout(() => setCopied(false), 2000);
  };

  if (loadErr) return <Notice icon={AlertTriangle} tone="text-amber-400" testid="sms-not-found" text={t("recipient.notFound")} />;
  if (!meta) return <Wrap><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div></Wrap>;
  if (step === "locked" || (meta.status === "locked" && step !== "done")) {
    return <Notice icon={ShieldX} tone="text-red-400" testid="sms-locked" text={t("recipient.locked")} />;
  }
  if (meta.status !== "active" && step !== "done") {
    return <Notice icon={Flame} tone="text-slate-500" testid="sms-unavailable"
      text={meta.status === "expired" ? t("recipient.expired") : t("recipient.consumed")} />;
  }

  return (
    <Wrap>
      <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8">
        {step === "code" && (
          <>
            <div className="flex items-center gap-2 mb-1"><Lock className="w-5 h-5 text-emerald-400" />
              <h1 className="font-heading text-xl font-bold text-white">{t("recipient.codeTitle")}</h1></div>
            <p className="text-sm text-slate-400 mb-6">
              {t("recipient.codeHelp")}
            </p>
            <OralCodeInput value={code} onChange={(v) => { setCode(v); setError(null); }} disabled={checking} onComplete={unlock} />
            <div className="min-h-[2.5rem] mt-4 text-center" aria-live="polite">
              {error && <p data-testid="unlock-error" className="text-sm text-red-400">{error}</p>}
              {remaining !== null && remaining < 5 && (
                <p data-testid="attempts-remaining" className="text-xs text-amber-400 mt-1">
                  {t("recipient.attemptsRemaining", { count: remaining })}
                </p>
              )}
            </div>
            <Button data-testid="unlock-btn" onClick={() => unlock()} disabled={code.length !== 9 || checking}
              className="w-full h-12 mt-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold text-base">
              {checking ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Unlock className="w-5 h-5 mr-2" />}
              {checking ? t("recipient.checking") : t("recipient.unlock")}
            </Button>
            <p className="text-[11px] text-slate-500 mt-4 text-center">{t("recipient.lockWarning")}</p>
          </>
        )}

        {step === "ready" && (
          <div className="text-center" data-testid="sms-ready">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
              <Unlock className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="font-heading text-xl font-bold text-white mb-2">{t("recipient.validated")}</h1>
            <p className="text-sm text-slate-400 mb-6">{t("recipient.readyHelp")}</p>
            <Button data-testid="reveal-btn" onClick={reveal} disabled={revealing}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold text-base">
              {revealing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Flame className="w-5 h-5 mr-2" />}
              {t("recipient.reveal")}
            </Button>
          </div>
        )}

        {step === "done" && (
          <div>
            <div className="flex items-center gap-2 mb-4"><Check className="w-5 h-5 text-emerald-400" />
              <h1 className="font-heading text-xl font-bold text-white">{t("recipient.revealedTitle")}</h1></div>
            <div data-testid="revealed-secret" className="ph-no-capture rounded-xl bg-slate-800 border border-slate-700 p-5 font-mono-token text-xl sm:text-2xl text-white break-all mb-3 leading-relaxed select-all">
              {secret}
            </div>
            <Button data-testid="copy-secret-btn" onClick={copySecret} className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold">
              {copied ? <Check className="w-5 h-5 mr-2" /> : <Copy className="w-5 h-5 mr-2" />}{copied ? t("recipient.copied") : t("recipient.copy")}
            </Button>
            <p className="text-xs text-amber-400/90 mt-4 text-center flex items-center justify-center gap-1.5">
              <Flame className="w-3.5 h-3.5" /> {t("recipient.destroyed")}
            </p>
          </div>
        )}
      </div>
    </Wrap>
  );
}
